package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.material.MaterialInteractionState
import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.MaskType
import top.wcpe.allincore.ui.runtime.style.StyleVisual

/**
 * 容器/Box 渲染器：根据 [top.wcpe.allincore.ui.runtime.style.Style] 绘制背景。
 *
 * ADR-0041 生产化：盒级 paint 效果（阴影 / 表面 / 程序纹理 / 霓虹 / 流光 / 能量）经
 * [top.wcpe.allincore.ui.runtime.style.VisualModifierChain] + [ModifierDispatcher] 分派，取代硬编码逐效果绘制——
 * **绘制顺序 = 链顺序**。为保留「表面后、效果前」插入 theme scanline 的现序，分两段分派
 * （base → theme scanline → effect）。theme scanline（主题态）与 ripple（RippleAnimator 动态态）保留 live。
 */
class BoxRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val style = element.style()
        val bg = style.backgroundColor()
        val visual = style.visual()
        val modifierChain = style.modifiers()
        val hasOpaqueBg = (bg ushr ALPHA_SHIFT) != 0
        val sink = RenderBridgeSink(bridge)
        val x = box.x()
        val y = box.y()
        val w = box.width()
        val h = box.height()

        // 元素当前交互态：驱动材质动态参数（FR-UI-024），静态材质层忽略之。
        val states = materialStatesOf(element)

        // 底层 paint（阴影 → 表面）
        ModifierDispatcher.dispatch(modifierChain.basePaint, x, y, w, h, sink, states)

        // 主题扫描线留 live（主题态，非 StyleVisual 静态）：现序在表面后、效果前；
        // 仅当容器有可见背景 / surface（styled 效果或不透明背景色）时叠加，避免给纯布局透明 box 铺扫描线。
        if (hasNonShadowStyledEffect(visual) || hasOpaqueBg) {
            EffectsCompositor.applyThemeScanlineIfConfigured(box, bridge, visual, null)
        }

        // 叠加 paint 效果（程序纹理 → 霓虹 → 流光 → 能量 → 材质层）
        ModifierDispatcher.dispatch(modifierChain.effectPaint, x, y, w, h, sink, states)

        // 涟漪留 live（RippleAnimator 动态态）
        EffectsCompositor.applyRippleIfActive(element, box, bridge)
    }

    /** 收集元素当前交互态：hovered / pressed / focused / disabled（FR-UI-024 动态材质驱动）。 */
    private fun materialStatesOf(element: UIElement<*>): Set<MaterialInteractionState> {
        val states = mutableSetOf<MaterialInteractionState>()
        if (element.hovered()) states += MaterialInteractionState.HOVERED
        if (element.pressed()) states += MaterialInteractionState.PRESSED
        if (element.focused()) states += MaterialInteractionState.FOCUSED
        if (!element.enabled()) states += MaterialInteractionState.DISABLED
        return states
    }

    /** 是否有阴影以外的 styled 效果（圆角 / 边框 / 渐变 / 遮罩）；用于 theme scanline 门控。 */
    private fun hasNonShadowStyledEffect(visual: StyleVisual): Boolean = visual.cornerRadiusTL() > 0f ||
        visual.cornerRadiusTR() > 0f ||
        visual.cornerRadiusBR() > 0f ||
        visual.cornerRadiusBL() > 0f ||
        visual.borderWidth() > 0f ||
        visual.gradientType() != GradientType.NONE ||
        visual.maskType() != MaskType.NONE

    private companion object {
        /** ARGB alpha 字节位移。 */
        private const val ALPHA_SHIFT = 24
    }
}
